import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  type ElementRef,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { fromEvent, map, startWith } from 'rxjs';
import { AuthStore } from '@/features/auth-store.service';
import { WebSocketClient } from '@/core/notification/websocket-client.service';

import { SidebarComponent } from '@/layout/sidebar/sidebar.component';
import { TopbarComponent } from '@/layout/topbar/topbar.component';
import { ToastLayerComponent } from '@/layout/toast-layer/toast-layer.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, SidebarComponent, TopbarComponent, ToastLayerComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './app.component.scss',
  templateUrl: './app.component.html',
})
export class AppComponent {
  private authStore = inject(AuthStore);
  private webSocketClient = inject(WebSocketClient);
  private readonly destroyRef = inject(DestroyRef);
  private readonly scrollContainer = viewChild.required<ElementRef<HTMLElement>>('scrollContainer');
  protected isAuthenticated = this.authStore.isAuthenticated;
  protected readonly isContentScrolled = signal(false);

  constructor() {
    effect(() => {
      if (this.isAuthenticated()) {
        this.webSocketClient.connect();
        return;
      }
      this.webSocketClient.disconnect();
    });

    afterNextRender(() => {
      this.trackContentScroll();
    });
  }

  private trackContentScroll(): void {
    const scrollContainer = this.scrollContainer().nativeElement;
    fromEvent(scrollContainer, 'scroll', { passive: true })
      .pipe(
        startWith(null),
        map(() => scrollContainer.scrollTop > 0),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((isContentScrolled) => {
        this.isContentScrolled.set(isContentScrolled);
      });
  }
}
